import React, { useState, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";

const REASONS = [
  { key: "wrong_component",     label: "Wrong Component" },
  { key: "missing_component",   label: "Missing Component" },
  { key: "damaged",             label: "Damaged" },
  { key: "wrong_specification", label: "Wrong Specification" },
  { key: "sku_mismatch",        label: "Serial Number / SKU Mismatch" },
  { key: "other",               label: "Other" },
];

export function ReportIssueModal({ open, onClose, orderId, componentKey, componentLabel, componentValue, onSubmitted }) {
  const [reason, setReason] = useState("wrong_component");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setReason("wrong_component"); setNotes(""); setBusy(false); }
  }, [open]);

  if (!open) return null;

  const otherRequiresNotes = reason === "other";
  const canSubmit = !busy && (!otherRequiresNotes || notes.trim().length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await client.post(`/orders/${orderId}/component-verifications/${componentKey}/report-issue`, {
        reason,
        notes: notes.trim() || null,
      });
      toast.success("Issue reported to supervisor");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to report issue");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)" }}
      onClick={onClose}
      data-testid="report-issue-modal"
    >
      <div
        className="rounded-xl border max-w-md w-full max-h-[92vh] overflow-y-auto"
        style={{ background: "#0d0d10", borderColor: "rgba(255,152,0,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 flex items-center gap-2.5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <AlertTriangle size={16} style={{ color: "#ff9800" }} />
          <div className="text-white text-sm font-semibold tracking-[0.22em] uppercase">Component Issue</div>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold">Component</div>
            <div className="mt-1 text-white text-sm font-semibold">{componentLabel}</div>
            <div className="text-white/60 text-sm">{componentValue}</div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-2">Reason</div>
            <div className="grid grid-cols-1 gap-1.5">
              {REASONS.map((r) => (
                <label
                  key={r.key}
                  className="flex items-center gap-2.5 p-2.5 rounded-md cursor-pointer border transition-colors"
                  style={{
                    borderColor: reason === r.key ? "rgba(255,152,0,0.45)" : "rgba(255,255,255,0.06)",
                    background:  reason === r.key ? "rgba(255,152,0,0.08)" : "transparent",
                  }}
                  data-testid={`issue-reason-${r.key}`}
                >
                  <input
                    type="radio"
                    name="issue-reason"
                    checked={reason === r.key}
                    onChange={() => setReason(r.key)}
                    className="accent-orange-400"
                  />
                  <span className="text-white text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-1.5">
              Notes {otherRequiresNotes && <span style={{ color: "#ff9800" }}>*</span>}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={otherRequiresNotes ? "Required — describe the issue." : "Optional details for the supervisor."}
              className="w-full rounded-md px-3 py-2 text-sm text-white outline-none"
              style={{ background: "#050507", border: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="issue-notes-input"
            />
          </div>
        </div>

        <div className="px-5 py-4 flex items-center justify-end gap-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold tracking-[0.18em] uppercase text-white/60 hover:text-white"
            data-testid="issue-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-md text-xs font-semibold tracking-[0.18em] uppercase disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: "rgba(255,152,0,0.9)", color: "#1a0f00" }}
            data-testid="issue-submit"
          >
            {busy ? "Submitting…" : "Submit Issue"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReportIssueModal;
